// ============================================================================
// Canonical Pig Batches route contracts — hub and record workspace
// ============================================================================
// `{view: 'pigbatches', paths: ['/pig/batches/*']}` is one splat route whose
// presenter selects between its hub and inline record workspace from pathname.
// A release must partition that space explicitly: the router accepts an empty
// suffix and malformed ids, but those are not audited release shapes.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const PIG_BATCHES_INDEX_PATHNAME = '/pig/batches';
export const PIG_BATCHES_DETAIL_PATHNAME_PREFIX = '/pig/batches/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// PigBatchPage reads only one query shape: `?trip=<id>` focuses an existing
// trip. Keep the grammar raw and closed so a second key, encoding, separator,
// or empty id cannot turn into a wider direct-entry contract.
const SAFE_TRIP_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

function isSafeRawSegment(segment: string): boolean {
  return !(
    segment === '' ||
    segment.includes('/') ||
    segment.includes('\\') ||
    segment.includes('\0') ||
    segment.includes('\r') ||
    segment.includes('\n') ||
    segment.includes('?') ||
    segment.includes('#')
  );
}

function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/** The hub's one audited direct-entry pathname. */
export function isCanonicalPigBatchesIndexPathname(pathname: unknown): pathname is string {
  return pathname === PIG_BATCHES_INDEX_PATHNAME;
}

/** `/pig/batches/<id>` with exactly one decoded-safe identity segment. */
export function isCanonicalPigBatchesDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(PIG_BATCHES_DETAIL_PATHNAME_PREFIX)) return false;
  const rawSegment = pathname.slice(PIG_BATCHES_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

// The hub does not inspect search or hash; its only location-based behavior is
// record mode, which cannot be entered on the exact base path.
export function isCanonicalPigBatchesIndexLocation(location: unknown): boolean {
  return isCanonicalPigBatchesIndexPathname(pathnameOf(location));
}

/** One exact, decoded-safe raw `?trip=<id>` focus carrier. */
export function isCanonicalPigBatchTripSearch(search: unknown): search is string {
  return typeof search === 'string' && search.startsWith('?trip=') && SAFE_TRIP_ID.test(search.slice('?trip='.length));
}

// The record workspace reads `?trip=<id>` to focus an existing trip. That one
// closed carrier is an audited render shape; unknown, repeated, or malformed
// query state and every hash remain outside the direct release.
export function isCanonicalPigBatchesDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalPigBatchesDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '' || isCanonicalPigBatchTripSearch(search)) &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalPigBatchesDetailPathname(pathname)
  );
}
